/**
 * Shared country reference list for lead-capture forms.
 *
 * FINAL ESTATE-WIDE LEAD CONTRACT (settled 2026-08-20):
 *   country_code — the DIAL code, e.g. "+968"   /^\+\d{1,4}$/
 *   country_iso  — ISO 3166-1 alpha-2 UPPERCASE, e.g. "OM"  /^[A-Z]{2}$/
 *   phone_e164   — the full E.164 number, e.g. "+96891234567"
 *
 * `country_code` carries the DIAL code because that is what the ZIVUX CRM lead
 * field actually holds — see proeffico/maflo_website:web/lib/zivux.ts, which
 * has always sent `country_code: input.country_code || '+91'`. The ISO-2 code
 * therefore travels under its own name, `country_iso`.
 *
 * Shape of the rows BELOW (internal to this module, not the wire contract):
 *   iso  — ISO 3166-1 alpha-2, UPPERCASE  ("OM")
 *   dial — E.164 calling code WITH the leading plus ("+968")
 *   name — English display name ("Oman")
 *   flag — emoji flag, used for the select label ("🇴🇲")
 *
 * NOTE — this is deliberately NOT the same list as src/contexts/CountryContext.jsx.
 * That one is a *billing region* selector whose values include the synthetic
 * 'GLOBAL' key used to look up rows in `pricing_plans`. It is not ISO-2 and must
 * not be used to populate a lead's country.
 *
 * Ordering is intentional: India first (primary market), then GCC, then the
 * remaining major markets alphabetically. Ordering drives the <select> order.
 */

export const COUNTRIES = [
  // Primary market
  { iso: 'IN', name: 'India',                dial: '+91',  flag: '🇮🇳' },

  // GCC
  { iso: 'OM', name: 'Oman',                 dial: '+968', flag: '🇴🇲' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '+971', flag: '🇦🇪' },
  { iso: 'SA', name: 'Saudi Arabia',         dial: '+966', flag: '🇸🇦' },
  { iso: 'QA', name: 'Qatar',                dial: '+974', flag: '🇶🇦' },
  { iso: 'KW', name: 'Kuwait',               dial: '+965', flag: '🇰🇼' },
  { iso: 'BH', name: 'Bahrain',              dial: '+973', flag: '🇧🇭' },

  // Other major markets
  { iso: 'AU', name: 'Australia',            dial: '+61',  flag: '🇦🇺' },
  { iso: 'BD', name: 'Bangladesh',           dial: '+880', flag: '🇧🇩' },
  { iso: 'CA', name: 'Canada',               dial: '+1',   flag: '🇨🇦' },
  { iso: 'DE', name: 'Germany',              dial: '+49',  flag: '🇩🇪' },
  { iso: 'EG', name: 'Egypt',                dial: '+20',  flag: '🇪🇬' },
  { iso: 'FR', name: 'France',               dial: '+33',  flag: '🇫🇷' },
  { iso: 'GB', name: 'United Kingdom',       dial: '+44',  flag: '🇬🇧' },
  { iso: 'ID', name: 'Indonesia',            dial: '+62',  flag: '🇮🇩' },
  { iso: 'KE', name: 'Kenya',                dial: '+254', flag: '🇰🇪' },
  { iso: 'LK', name: 'Sri Lanka',            dial: '+94',  flag: '🇱🇰' },
  { iso: 'MY', name: 'Malaysia',             dial: '+60',  flag: '🇲🇾' },
  { iso: 'NG', name: 'Nigeria',              dial: '+234', flag: '🇳🇬' },
  { iso: 'NP', name: 'Nepal',                dial: '+977', flag: '🇳🇵' },
  { iso: 'NZ', name: 'New Zealand',          dial: '+64',  flag: '🇳🇿' },
  { iso: 'PH', name: 'Philippines',          dial: '+63',  flag: '🇵🇭' },
  { iso: 'SG', name: 'Singapore',            dial: '+65',  flag: '🇸🇬' },
  { iso: 'US', name: 'United States',        dial: '+1',   flag: '🇺🇸' },
  { iso: 'ZA', name: 'South Africa',         dial: '+27',  flag: '🇿🇦' },
];

/** Default when detection fails or returns a country we do not list. */
export const DEFAULT_COUNTRY_ISO = 'IN';

/**
 * Look a country up by ISO-2 code. Case-insensitive, whitespace tolerant.
 * Returns undefined for unknown / empty input — callers must treat a missing
 * country as optional and never block submission on it.
 */
export function findByIso(iso) {
  const code = String(iso || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return undefined;
  return COUNTRIES.find(c => c.iso === code);
}

/** "🇴🇲 Oman +968" — the label rendered in every country <select>. */
export function countryLabel(c) {
  return `${c.flag} ${c.name} ${c.dial}`;
}

/**
 * Build the { country_code, country_iso, phone_e164 } triple sent with a lead.
 *
 *   country_code — DIAL code  ("+968")   country_iso — ISO-2 ("OM")
 *
 * All three fields are OPTIONAL by contract. If the country is unknown we
 * return nulls rather than throwing, so a lead is never lost because the
 * IP lookup failed or the visitor is somewhere we do not list.
 *
 * phone_e164 is only produced when it can be derived with confidence:
 *   - the user already typed a full international number (+968 9123 4567)
 *     → normalised to +96891234567
 *   - the user typed a national number             (9123 4567, country OM)
 *     → prefixed with the dial code  → +96891234567
 * A leading trunk '0' is stripped, which is correct for every country in the
 * list above. If the digits do not look like a phone number at all (<7 or
 * >15 digits per E.164) we return null instead of a bogus value.
 */
export function buildCountryFields(iso, rawPhone) {
  const country = findByIso(iso);
  if (!country) return { country_code: null, country_iso: null, phone_e164: null };

  const raw = String(rawPhone || '').trim();
  let phone_e164 = null;

  if (raw) {
    if (raw.startsWith('+')) {
      const digits = raw.replace(/\D/g, '');
      if (digits.length >= 7 && digits.length <= 15) phone_e164 = `+${digits}`;
    } else {
      const national = raw.replace(/\D/g, '').replace(/^0+/, '');
      const dialDigits = country.dial.replace(/\D/g, '');
      if (national && (dialDigits + national).length >= 7 && (dialDigits + national).length <= 15) {
        phone_e164 = `+${dialDigits}${national}`;
      }
    }
  }

  // country_code = DIAL code, country_iso = ISO-2. See the contract note above.
  return { country_code: country.dial, country_iso: country.iso, phone_e164 };
}
